import { useEffect, useMemo, useState } from "react";
import { useSearchParams, useParams, useNavigate } from "react-router-dom";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { SlidersHorizontal, X, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Reveal, EASE_OUT } from "@/components/motion/Reveal";
import { ProductCard, ProductCardSkeleton } from "@/components/ProductCard";
import { TileImage } from "@/components/TileImage";
import { FilterSidebar } from "@/components/FilterSidebar";
import { getProducts, getMeta, getBrands, getCategories } from "@/lib/api";
import { gradeShort } from "@/lib/format";
import { CATEGORY_LABELS } from "@/data/content";

const SORT_OPTIONS = [
  { value: "evidenza", label: "In evidenza" },
  { value: "novita", label: "Novità" },
  { value: "prezzo_asc", label: "Prezzo crescente" },
  { value: "prezzo_desc", label: "Prezzo decrescente" },
];

const EMPTY_FILTERS = { brands: [], categories: [], grades: [], price: [0, 0] };

function defaultFilters(meta, params) {
  return {
    brands: params.brand ? [params.brand.toUpperCase()] : [],
    categories: params.category ? [params.category] : [],
    grades: [],
    price: [meta.price_min, meta.price_max],
  };
}

const brandItem = {
  hidden: { opacity: 0, y: 24 },
  show: (i) => ({ opacity: 1, y: 0, transition: { duration: 0.5, delay: (i % 8) * 0.05, ease: [0.16, 1, 0.3, 1] } }),
};

// Box marchio con foto profilo (dal DB), profondità e micro-animazioni come le categorie.
function BrandTile({ brand, onGo, index = 0 }) {
  const reduce = useReducedMotion();
  return (
    <motion.div
      custom={index}
      variants={reduce ? undefined : brandItem}
      initial={reduce ? false : "hidden"}
      whileInView={reduce ? undefined : "show"}
      viewport={{ once: true, margin: "-60px" }}
    >
      <button
        type="button"
        onClick={() => onGo(brand.key)}
        className="group relative block w-full text-left focus-visible:outline-none"
        data-testid={`brand-tile-${brand.key.toLowerCase()}`}
      >
        <span aria-hidden="true" className="pointer-events-none absolute -inset-1.5 rounded-[26px] bg-lime/25 opacity-0 blur-xl transition-opacity duration-300 group-hover:opacity-100" />
        <div className="relative flex aspect-[4/3] items-center justify-center overflow-hidden rounded-2xl bg-lime shadow-[0_8px_24px_-12px_rgba(0,0,0,0.6)] transition-transform duration-300 ease-out will-change-transform group-hover:-translate-y-1.5 group-active:translate-y-0">
          <div className="absolute inset-0 transition-transform duration-[600ms] ease-out will-change-transform group-hover:scale-[1.07]">
            {brand.logo ? (
              <TileImage url={brand.logo} alt={brand.name || brand.key} scale={brand.image_scale} fit={brand.image_fit} posX={brand.image_pos_x} posY={brand.image_pos_y} />
            ) : (
              <span className="flex h-full w-full items-center justify-center px-3 text-center font-display text-xl font-extrabold text-ink/70">{brand.name || brand.key}</span>
            )}
          </div>
          <span aria-hidden="true" className="pointer-events-none absolute inset-x-0 top-0 h-1/3 bg-gradient-to-b from-white/15 to-transparent opacity-50" />
          <span aria-hidden="true" className="pointer-events-none absolute inset-y-0 -left-1/3 w-1/3 -skew-x-12 bg-gradient-to-r from-transparent via-white/30 to-transparent opacity-0 transition-[left,opacity] duration-700 ease-out group-hover:left-[110%] group-hover:opacity-100" />
          <span aria-hidden="true" className="pointer-events-none absolute inset-0 rounded-2xl ring-1 ring-inset ring-black/10" />
        </div>
        <div className="mt-3 flex items-center justify-between gap-2">
          <span className="text-sm font-bold text-foreground transition-colors duration-200 group-hover:text-lime sm:text-base">{brand.name || brand.key}</span>
          <span className="font-mono text-[10px] uppercase tracking-mono text-tertiary">{String(brand.count).padStart(2, "0")}</span>
        </div>
      </button>
    </motion.div>
  );
}

export default function Catalog() {
  const [searchParams] = useSearchParams();
  const { brand: brandSlug } = useParams();
  const navigate = useNavigate();
  const search = searchParams.get("search") || "";
  const categoryParam = searchParams.get("category") || "";

  const [meta, setMeta] = useState(null);
  const [allProducts, setAllProducts] = useState(null);
  const [filters, setFilters] = useState(null);
  const [sort, setSort] = useState("evidenza");
  const [dbBrands, setDbBrands] = useState(null);
  const [dbCategories, setDbCategories] = useState(null);

  useEffect(() => {
    getBrands().then(setDbBrands).catch(() => setDbBrands([]));
    getCategories().then(setDbCategories).catch(() => setDbCategories([]));
  }, []);

  useEffect(() => {
    getMeta().then((m) => {
      setMeta(m);
      setFilters(defaultFilters(m, { brand: brandSlug, category: categoryParam }));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (meta) setFilters(defaultFilters(meta, { brand: brandSlug, category: categoryParam }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandSlug, categoryParam]);

  useEffect(() => {
    getProducts({ search, limit: 200 }).then(setAllProducts).catch(() => setAllProducts([]));
  }, [search]);

  const filtered = useMemo(() => {
    if (!allProducts || !filters) return [];
    const list = allProducts.filter((p) => {
      if (filters.brands.length && !filters.brands.includes(p.brand)) return false;
      if (filters.categories.length && !filters.categories.includes(p.category)) return false;
      if (filters.grades.length && !filters.grades.some((g) => (p.available_grades || []).includes(g))) return false;
      const price = p.min_price || 0;
      if (price < filters.price[0] || price > filters.price[1]) return false;
      return true;
    });
    const s = [...list];
    if (sort === "prezzo_asc") s.sort((a, b) => (a.min_price || 0) - (b.min_price || 0));
    else if (sort === "prezzo_desc") s.sort((a, b) => (b.min_price || 0) - (a.min_price || 0));
    else if (sort === "novita") s.sort((a, b) => (b.sort_order || 0) - (a.sort_order || 0));
    else s.sort((a, b) => (a.featured === b.featured ? (b.sort_order || 0) - (a.sort_order || 0) : a.featured ? -1 : 1));
    return s;
  }, [allProducts, filters, sort]);

  const clearFilters = () => setFilters(defaultFilters(meta, {}));

  // Modalità: marchi (ingresso) oppure prodotti (filtri/categoria/marca/ricerca attivi).
  const showProducts = Boolean(
    brandSlug || search ||
    (filters && (filters.brands.length || filters.categories.length || filters.grades.length))
  );

  const goToBrand = (key) => navigate(`/catalogo/${key.toLowerCase()}`);
  const selectCategory = (key) => setFilters((f) => ({ ...(f || EMPTY_FILTERS), categories: [key] }));
  const backToBrands = () => {
    if (brandSlug || search) navigate("/catalogo");
    clearFilters();
  };

  const activeChips = [];
  if (filters) {
    filters.brands.forEach((b) => activeChips.push({ type: "brands", value: b, label: b }));
    filters.categories.forEach((c) => activeChips.push({ type: "categories", value: c, label: CATEGORY_LABELS[c] || c }));
    filters.grades.forEach((g) => activeChips.push({ type: "grades", value: g, label: gradeShort(g) }));
  }
  const removeChip = (chip) => setFilters((f) => ({ ...f, [chip.type]: f[chip.type].filter((v) => v !== chip.value) }));

  const loading = allProducts === null || filters === null || meta === null;
  const brands = dbBrands || [];
  const brandsLoading = dbBrands === null;
  const currentBrandLabel = brandSlug ? brandSlug.toUpperCase() : null;

  return (
    <div className="container-page pb-24 pt-10 sm:pt-14" data-testid="catalog-page">
      <Reveal className="flex flex-col gap-6 border-b border-border pb-10 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <span className="section-index">Catalogo</span>
          <h1 className="display-xl mt-4 text-foreground">
            {search ? `“${search}”` : currentBrandLabel ? currentBrandLabel : showProducts ? "Prodotti" : "Scegli un marchio"}
          </h1>
          <p className="mt-4 max-w-xl text-base text-muted-foreground sm:text-lg">
            {showProducts
              ? "Nuovo e ricondizionato, ogni prodotto verificato su 32 punti di controllo."
              : "Scegli un marchio dai box qui sotto, oppure parti da una categoria."}
          </p>
        </div>
        {showProducts && (
          <div className="font-mono text-[11px] uppercase tracking-mono text-muted-foreground" data-testid="catalog-results-count">
            {loading ? "Caricamento" : `${String(filtered.length).padStart(2, "0")} prodotti`}
          </div>
        )}
      </Reveal>

      <AnimatePresence mode="wait" initial={false}>
        {!showProducts ? (
          /* ---------------- VISTA MARCHI ---------------- */
          <motion.div
            key="brands"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.4, ease: EASE_OUT }}
            className="mt-10 grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-12"
            data-testid="catalog-brands-view"
          >
            {/* Colonna categorie (percorso alternativo) — dal database */}
            <aside className="lg:col-span-3">
              <div className="mono-label text-tertiary">Per categoria</div>
              <ul className="mt-4 border-t border-border">
                {(dbCategories || []).map((c) => (
                  <li key={c.key}>
                    <button
                      type="button"
                      onClick={() => selectCategory(c.key)}
                      className="group flex w-full items-center justify-between border-b border-border py-3 text-left transition-colors duration-200"
                      data-testid={`catalog-category-${c.key}`}
                    >
                      <span className="text-[15px] text-foreground transition-transform duration-300 ease-out-expo group-hover:translate-x-1">{c.label}</span>
                      <span className="font-mono text-[10px] uppercase tracking-mono text-tertiary">{String(c.count).padStart(2, "0")}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </aside>

            {/* Griglia marchi con foto profilo (tutti i marchi, ordine da admin) */}
            <div className="lg:col-span-9">
              {brandsLoading ? (
                <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
                  {Array.from({ length: 8 }).map((_, i) => <div key={i} className="aspect-[4/3] rounded-2xl bg-surface" />)}
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 sm:gap-5 lg:grid-cols-4 lg:gap-6" data-testid="catalog-brands-grid">
                  {brands.map((b, i) => (
                    <BrandTile key={b.key} brand={b} onGo={goToBrand} index={i} />
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        ) : (
          /* ---------------- VISTA PRODOTTI ---------------- */
          <motion.div
            key="products"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, ease: EASE_OUT }}
            className="mt-8 grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-12"
            data-testid="catalog-products-view"
          >
            <aside className="hidden lg:col-span-3 lg:block">
              <div className="sticky top-[100px]">
                <button type="button" onClick={backToBrands} className="mb-6 inline-flex items-center gap-2 font-mono text-[10.5px] uppercase tracking-mono text-muted-foreground transition-colors hover:text-lime" data-testid="catalog-back-to-brands">
                  <ArrowLeft className="h-3.5 w-3.5" strokeWidth={1.5} /> Torna ai marchi
                </button>
                <div className="mono-label pb-2 text-foreground">Filtri</div>
                <FilterSidebar meta={meta} filters={filters || EMPTY_FILTERS} setFilters={setFilters} onClear={clearFilters} />
              </div>
            </aside>

            <div className="lg:col-span-9">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <Sheet>
                    <SheetTrigger asChild>
                      <Button
                        variant="lime"
                        size="lg"
                        className="fixed bottom-5 left-1/2 z-40 -translate-x-1/2 rounded-full shadow-soft lg:hidden"
                        data-testid="catalog-open-filters-button"
                      >
                        <SlidersHorizontal strokeWidth={1.5} /> Filtri
                      </Button>
                    </SheetTrigger>
                    <SheetContent side="left" className="w-[86vw] max-w-sm overflow-y-auto border-line bg-ink">
                      <SheetHeader className="text-left">
                        <SheetTitle className="mono-label text-foreground">Filtri</SheetTitle>
                        <SheetDescription className="sr-only">Filtra il catalogo per marca, categoria, condizione e prezzo</SheetDescription>
                      </SheetHeader>
                      <button type="button" onClick={backToBrands} className="mt-4 inline-flex items-center gap-2 font-mono text-[10.5px] uppercase tracking-mono text-muted-foreground hover:text-lime">
                        <ArrowLeft className="h-3.5 w-3.5" strokeWidth={1.5} /> Torna ai marchi
                      </button>
                      <div className="mt-4">
                        <FilterSidebar meta={meta} filters={filters || EMPTY_FILTERS} setFilters={setFilters} onClear={clearFilters} />
                      </div>
                    </SheetContent>
                  </Sheet>
                  <button type="button" onClick={backToBrands} className="hidden items-center gap-2 font-mono text-[10.5px] uppercase tracking-mono text-muted-foreground transition-colors hover:text-lime lg:inline-flex" data-testid="catalog-back-to-brands-top">
                    <ArrowLeft className="h-3.5 w-3.5" strokeWidth={1.5} /> Torna ai marchi
                  </button>
                </div>

                <Select value={sort} onValueChange={setSort}>
                  <SelectTrigger className="h-10 w-[200px] rounded-full border-border bg-card px-4 text-sm" data-testid="catalog-sort-select">
                    <SelectValue placeholder="Ordina" />
                  </SelectTrigger>
                  <SelectContent className="border-line bg-surface">
                    {SORT_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <AnimatePresence initial={false}>
                {activeChips.length > 0 && (
                  <motion.div className="mt-5 flex flex-wrap gap-2" initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }} transition={{ duration: 0.25, ease: EASE_OUT }}>
                    {activeChips.map((chip) => (
                      <button key={`${chip.type}-${chip.value}`} type="button" onClick={() => removeChip(chip)}
                        className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1.5 font-mono text-[10.5px] uppercase tracking-mono text-foreground transition-[border-color,color] duration-200 hover:border-lime hover:text-lime"
                        data-testid="active-filter-chip" aria-label={`Rimuovi filtro ${chip.label}`}>
                        {chip.label}<X className="h-3 w-3" strokeWidth={2} />
                      </button>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>

              {loading ? (
                <div className="mt-8 grid grid-cols-2 gap-4 sm:gap-5 md:grid-cols-3">
                  {Array.from({ length: 6 }).map((_, i) => <ProductCardSkeleton key={i} />)}
                </div>
              ) : filtered.length === 0 ? (
                <motion.div className="mt-10 rounded border border-border bg-card p-10" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} data-testid="empty-state">
                  <div className="eyebrow">Nessun risultato</div>
                  <p className="mt-4 max-w-md text-base text-muted-foreground">Nessun prodotto corrisponde ai filtri selezionati. Prova a rimuovere un filtro.</p>
                  <Button variant="outline" size="sm" className="mt-6" onClick={clearFilters} data-testid="empty-state-clear-button">Rimuovi filtri</Button>
                </motion.div>
              ) : (
                <motion.div layout className="mt-8 grid grid-cols-2 gap-4 sm:gap-5 md:grid-cols-3" data-testid="catalog-grid">
                  <AnimatePresence mode="popLayout" initial={false}>
                    {filtered.map((p) => (
                      <motion.div key={p.id} layout initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.97 }} transition={{ duration: 0.3, ease: EASE_OUT }} className="h-full">
                        <ProductCard product={p} />
                      </motion.div>
                    ))}
                  </AnimatePresence>
                </motion.div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
